import type { CardFact } from '@game-platform/pack-animation';
import type { IframePoolOrderItem } from '../../api';

/** A card title as the catalog writes it opens with the set's year: "2000 Topps Chrome …". */
const LEADING_YEAR = /^\s*((?:19|20)\d{2})\b/;

/** "… PSA 9", "BGS 9.5", "SGC 10" — the slab's own label, as titles carry it. */
const TITLE_GRADE = /\b(PSA|BGS|SGC|CGC|CSG|HGA)\s*(10|\d(?:\.5)?)\b/i;

/** A line has to fit the scene's width; anything this long is prose, not a fact. */
const MAX_VALUE = 40;

type Source = Pick<IframePoolOrderItem, 'title' | 'badge' | 'cardDetails'>;

/**
 * What the carousel tells about the pull before it shows it.
 *
 * The card's own description (`cardDetails`, asked for per request — see
 * `cardDetailsQuery`) answers first: it is the catalog's, it is filled for
 * every tier, and its shape follows the card's category, so each fact is read
 * from the few places that category could have put it. Where it says nothing
 * — a card not enriched yet, or a category that is neither sport nor Pokémon
 * — the title and the badge answer instead: both carry the year, and a graded
 * card wears its grade in its own name.
 *
 * A line nothing can fill is left out. The story is as long as the facts, and
 * a blank line would be a fact of nothing.
 */
export function cardFactsFor(item: Source): CardFact[] {
  const d = item.cardDetails;
  const title = item.title ?? '';

  const facts: CardFact[] = [];
  const add = (label: string, value: string | null) => {
    if (value) facts.push({ label, value });
  };

  add('Year', pick(d, ['attributes.year', 'card.slab_year']) ?? year(title));
  add('Category', pick(d, ['attributes.category', 'category', 'attributes.sport']));
  add(
    'Featuring',
    pick(d, ['attributes.player', 'card.player', 'pokemon.character', 'name']) ??
      text(item.badge?.character),
  );
  add('Grade', grade(d) ?? text(item.badge?.grade) ?? titleGrade(title));

  return facts;
}

/** The first of these paths the description actually fills. */
function pick(details: Record<string, unknown> | null, paths: string[]): string | null {
  if (!details) return null;
  for (const path of paths) {
    const value = text(read(details, path));
    if (value) return value;
  }
  return null;
}

function read(details: Record<string, unknown>, path: string): unknown {
  let node: unknown = details;
  for (const key of path.split('.')) {
    if (node === null || typeof node !== 'object') return null;
    node = (node as Record<string, unknown>)[key];
  }
  return node;
}

/** The grading company and its number are two fields and one fact: "PSA 9". */
function grade(details: Record<string, unknown> | null): string | null {
  const company = pick(details, ['card.grade.company']);
  const value = pick(details, ['card.grade.grade']);
  if (!value) return company;
  return company ? `${company} ${value}` : value;
}

/** A string (or a number the enrichment left as one), trimmed to one line. */
function text(raw: unknown): string | null {
  const value =
    typeof raw === 'string' ? raw : typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : '';
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed && trimmed.length <= MAX_VALUE ? trimmed : null;
}

function year(title: string): string | null {
  return LEADING_YEAR.exec(title)?.[1] ?? null;
}

function titleGrade(title: string): string | null {
  const found = TITLE_GRADE.exec(title);
  return found ? `${found[1]!.toUpperCase()} ${found[2]}` : null;
}
